/**
 * Sentences for the Delivery & Returns page, Terms of Service and product FAQ,
 * filled in from the live delivery policy. The wording is the approved copy;
 * only the figures, countries and which sentences apply come from the data, so
 * a change in ERP Shop settings shows on the pages without a release.
 */
import type { DeliveryDays, DeliveryPolicy, DeliveryRegion } from '@/services/api'

const NAMES_WITH_THE = new Set(['United Kingdom', 'United States', 'Netherlands'])
const NUMBER_WORDS = ['No', 'One', 'Two', 'Three', 'Four', 'Five', 'Six']
// Adjectives for "Every US order"; other countries read "Every order to Canada".
const COUNTRY_ADJECTIVES: Record<string, string> = { US: 'US', GB: 'UK' }

export const withThe = (name: string) => (NAMES_WITH_THE.has(name) ? `the ${name}` : name)

/** "a, b, and c", with the serial comma the legal pages use. */
export const listJoin = (items: string[]) => {
  if (items.length <= 1) return items.join('')
  if (items.length === 2) return `${items[0]} and ${items[1]}`
  return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`
}

export const businessDays = (days: DeliveryDays | null | undefined) => {
  if (!days) return ''
  if (days.min === days.max) return `${days.min} business day${days.min === 1 ? '' : 's'}`
  return `${days.min} to ${days.max} business days`
}

const regionNames = (regions: DeliveryRegion[]) => listJoin(regions.flatMap((region) => region.countries.map((country) => withThe(country.name))))

export const homeRegion = (policy: DeliveryPolicy) => policy.regions.find((region) => region.duties === 'domestic')
export const internationalRegions = (policy: DeliveryPolicy) => policy.regions.filter((region) => region.duties !== 'domestic')
const regionsWith = (policy: DeliveryPolicy, duties: DeliveryRegion['duties']) => policy.regions.filter((region) => region.duties === duties)

/**
 * "the United Kingdom, the United States, Canada, Australia, and across Europe
 * (Austria, Belgium, ...)". Pass ``includeHome: false`` for "As well as the UK, we ship to ...".
 */
export const shippingCountriesPhrase = (policy: DeliveryPolicy, { includeHome = true } = {}) => {
  const countries = policy.countries.filter((country) => includeHome || country.code !== 'GB')
  const europe = countries.filter((country) => country.group === 'EUROPE').map((country) => withThe(country.name))
  const others = countries.filter((country) => country.group !== 'EUROPE').map((country) => withThe(country.name))
  return listJoin(europe.length ? [...others, `across Europe (${listJoin(europe)})`] : others)
}

/** The UK's Shipping Options list. */
export const homeShippingLines = (region: DeliveryRegion) => {
  const lines: { label: string; text: string }[] = []
  const standard = region.standard
  if (standard?.freeOver) {
    lines.push({ label: 'Free Delivery', text: `For orders over ${standard.freeOver.formatted}, standard delivery is free.` })
  }
  if (standard) {
    const under = standard.freeOver ? ` for orders under ${standard.freeOver.formatted}` : ''
    lines.push({
      label: 'Standard Delivery',
      text: `${standard.price.formatted}${under}, with an estimated ${businessDays(standard.deliveryDays)} for delivery after production.`,
    })
  }
  for (const option of region.options) {
    const when = option.label === 'Next Day Delivery' ? 'next business day' : businessDays(option.deliveryDays)
    lines.push({ label: option.label, text: `${option.price.formatted} for ${when} delivery after production.` })
  }
  return lines
}

/**
 * A region's name in the page's style. The backend sends a group's own name
 * ("Europe") when all of it shares the same facts, otherwise its countries
 * joined plainly, which become "Denmark, Germany, the Netherlands, and Sweden".
 */
export const regionLabel = (region: DeliveryRegion) => {
  const names = region.countries.map((country) => country.name)
  const plain = names.length > 1 ? `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}` : names[0]
  if (names.length > 1 && region.label === plain) return listJoin(names.map(withThe))
  return region.label
}

/** One International Shipping line, e.g. the United States or most of Europe. */
export const internationalLine = (region: DeliveryRegion) => {
  const standard = region.standard
  const parts: string[] = []
  if (standard) {
    parts.push(standard.freeOver
      ? `Free Standard Shipping for orders over ${standard.freeOver.formatted}, otherwise ${standard.price.formatted}.`
      : `Standard Shipping ${standard.price.formatted}.`)
    parts.push(`${region.upgrades.length ? 'Standard delivery' : 'Delivery'} in ${businessDays(standard.deliveryDays)} after production.`)
  }
  if (region.upgrades.length) {
    const upgrades = listJoin(region.upgrades.map((upgrade) => `${upgrade.name} (${businessDays(upgrade.deliveryDays)})`))
    const count = NUMBER_WORDS[region.upgrades.length] || String(region.upgrades.length)
    parts.push(`${count} paid upgrade${region.upgrades.length === 1 ? ' is' : 's are'} available: ${upgrades}. Upgrade prices are set at checkout and vary by parcel.`)
  }
  return { label: regionLabel(region), text: parts.join(' ') }
}

const everyOrder = (region: DeliveryRegion) => {
  const [only] = region.countries
  const adjective = region.countries.length === 1 ? COUNTRY_ADJECTIVES[only.code] : undefined
  return adjective ? `Every ${adjective} order` : `Every order to ${regionNames([region])}`
}

/** The duties paragraphs, one per way duties are handled, in the approved wording. */
export const dutiesParagraphs = (policy: DeliveryPolicy) => {
  const paragraphs: string[] = []
  const prepaid = regionsWith(policy, 'prepaid')
  if (prepaid.length) {
    const allEurope = prepaid.every((region) => region.group === 'EUROPE')
    paragraphs.push(`For ${allEurope ? 'European countries' : regionNames(prepaid)}, we offer DDP (Delivered Duty Paid) shipping, which includes pre-paid taxes and duties. This means you won't have to pay for duties and taxes before receiving your headphones. The pre-paid taxes and duties are charged as a line item on your order.`)
  }
  for (const region of regionsWith(policy, 'duties_paid')) {
    const fee = region.dutiesHandlingFee
    paragraphs.push([
      `For ${regionNames([region])}, orders are shipped with the duties paid (Delivered Duty Paid). We pay the applicable import duties, so nothing is collected from you on arrival.`,
      fee ? `${everyOrder(region)} has a flat ${fee.formatted} ${fee.label}, shown as its own line at checkout. It is not a payment of the duties themselves.` : '',
    ].filter(Boolean).join(' '))
  }
  const onArrival = regionsWith(policy, 'on_arrival')
  if (onArrival.length) {
    const canada = onArrival.some((region) => region.countries.some((country) => country.code === 'CA'))
    paragraphs.push(`For ${regionNames(onArrival)}, orders are shipped without pre-paid duties. Any import taxes, duties, or customs handling fees charged by the destination country${canada ? ' (for example GST/HST in Canada)' : ''} are payable by you to the carrier on arrival, and are not included in the price you pay at checkout.`)
  }
  return paragraphs
}

/** The courier sentence: upgrades name their courier, Standard leaves the choice to us. */
export const carrierSentence = (policy: DeliveryPolicy) => {
  const upgrades = policy.regions.flatMap((region) => region.upgrades.map((upgrade) => upgrade.name))
  if (!upgrades.length) {
    return 'We choose the carrier for each order and may use a different one from the service name shown at checkout, provided it meets the delivery estimate you selected.'
  }
  const byCourier = new Map<string, string[]>()
  for (const name of upgrades) {
    const [courier, ...service] = name.split(' ')
    byCourier.set(courier, [...(byCourier.get(courier) || []), service.join(' ')])
  }
  const named = Array.from(byCourier, ([courier, services]) => `${courier} for ${listJoin(services)}`)
  return `On paid upgrades, we use the courier named in the upgrade: ${named.join(', and ')}. On Standard shipping we choose the carrier for each order and may use a different one from the service name shown, provided it meets the delivery estimate you selected.`
}

/** The duties-paid sentence for returns, or '' when no country has a handling fee. */
export const returnsFeeSentence = (policy: DeliveryPolicy) => {
  const region = regionsWith(policy, 'duties_paid').find((candidate) => candidate.dutiesHandlingFee)
  if (!region?.dutiesHandlingFee) return ''
  const [only] = region.countries
  const who = region.countries.length === 1 && COUNTRY_ADJECTIVES[only.code] ? `a ${COUNTRY_ADJECTIVES[only.code]} return` : `a return from ${regionNames([region])}`
  return `Where we accept ${who}, the ${region.dutiesHandlingFee.formatted} ${region.dutiesHandlingFee.label} and the duty are not refunded.`
}

/** Terms section 4, Taxes. */
export const termsTaxesSentence = (policy: DeliveryPolicy) => {
  const region = regionsWith(policy, 'duties_paid')[0]
  if (!region) {
    return 'UK prices include applicable VAT. For overseas orders, the applicable tax treatment and any destination import taxes or duties depend on the order and delivery arrangements.'
  }
  const fee = region.dutiesHandlingFee
  return [
    'UK prices include applicable VAT. For overseas orders, the applicable tax treatment depends on the order and delivery arrangements.',
    `For ${regionNames([region])}, orders ship with the applicable import duties paid (Delivered Duty Paid), so nothing is collected on arrival.`,
    fee ? `${everyOrder(region)} includes a flat ${fee.formatted} ${fee.label}, shown as its own line at checkout, which is not a payment of the duties themselves.` : '',
  ].filter(Boolean).join(' ')
}

/** The product FAQ's note on import charges. */
export const faqDutiesSentence = (policy: DeliveryPolicy) => {
  const dutiesPaid = regionsWith(policy, 'duties_paid')
  if (!dutiesPaid.length) {
    return 'Please note that orders outside the UK may be subject to additional VAT, import taxes, or customs fees.'
  }
  const who = dutiesPaid.length === 1 && dutiesPaid[0].countries.length === 1 && COUNTRY_ADJECTIVES[dutiesPaid[0].countries[0].code]
    ? `${COUNTRY_ADJECTIVES[dutiesPaid[0].countries[0].code]} orders`
    : `Orders to ${regionNames(dutiesPaid)}`
  return `${who} ship with the import duties paid, so nothing is collected on arrival. Orders to other countries outside the UK may be subject to additional VAT, import taxes, or customs fees.`
}

/**
 * The product page's Delivery Information panel (HTML) for one country: its
 * delivery lines and duties from the delivery page, between the panel's own
 * intro and production notes. Null when the country isn't in the policy.
 */
export const productDeliveryInfoHtml = (policy: DeliveryPolicy, countryCode: string) => {
  const code = countryCode.toUpperCase()
  const region = policy.regions.find((candidate) => candidate.countries.some((country) => country.code === code))
  if (!region) return null
  const lines = region.duties === 'domestic' ? homeShippingLines(region) : [internationalLine(region)]
  return [
    "<p>We want to make sure your Kibu headphones arrive safely and as quickly as possible! Here's what you need to know about delivery:</p>",
    `<ul>${lines.map((line) => `<li><strong>${line.label}:</strong> ${line.text}</li>`).join('')}</ul>`,
    ...dutiesParagraphs({ ...policy, regions: [region] }).map((paragraph) => `<p>${paragraph}</p>`),
    '<h3>Why Production?</h3>',
    '<p>When you order Kibu headphones, we make them just for you using special 3D printers. This way, nothing goes to waste, and every pair is created fresh and ready to be built by you! Production usually takes up to <strong>5 working days</strong> before we send your headphones out for delivery.</p>',
    "<p>So whether you go for standard or faster delivery, your headphones are worth the wait—they're made with care and built just for you!</p>",
  ].join('')
}

/** The checkout fee line's info panel (HTML), in the approved US duties wording. */
export const handlingFeeExplanation = (formattedAmount: string) => [
  '<p>We pay the import duties on your US order ourselves, so nothing is collected when it arrives.</p>',
  `<p>There is one flat ${formattedAmount} import &amp; handling fee per order, not per pair: our fee for handling the import process, not a payment of the duties.</p>`,
].join('')

const ordinal = (day: number) => {
  const suffix = day % 10 === 1 && day !== 11 ? 'st' : day % 10 === 2 && day !== 12 ? 'nd' : day % 10 === 3 && day !== 13 ? 'rd' : 'th'
  return `${day}${suffix}`
}

/** "2nd November 2026": the later of the page's own update and the last delivery settings change. */
export const lastUpdated = (pageUpdated: Date, policy: DeliveryPolicy | null) => {
  const settingsUpdated = policy?.updatedAt ? new Date(`${policy.updatedAt}Z`) : null
  const date = settingsUpdated && settingsUpdated > pageUpdated ? settingsUpdated : pageUpdated
  return `${ordinal(date.getUTCDate())} ${date.toLocaleString('en-GB', { month: 'long', timeZone: 'UTC' })} ${date.getUTCFullYear()}`
}
